import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import Video from '@plone-collective/volto-techevent/components/Video/Video';
import SessionMaterials from '@plone-collective/volto-techevent/components/Schedule/SessionMaterials';
import messages from '@plone-collective/volto-techevent/messages';
import SessionChips from '../Session/SessionChips';
import { asList, getSpeakerImageUrl } from '../Session/utils';

const localMessages = defineMessages({
  speakers: { id: 'Speakers', defaultMessage: 'Speakers' },
});

const SessionView = ({ content }) => {
  const intl = useIntl();
  const track = asList(content.session_track)[0];

  const renderMetadataLine = () => {
    if (!content.start) return null;
    const start = new Date(content.start);
    const dateStr = start.toLocaleDateString(intl.locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const timeStr = start.toLocaleTimeString(intl.locale, {
      hour: '2-digit',
      minute: '2-digit',
    });

    let parts = [dateStr, timeStr];
    if (content.room?.title) {
      parts.push(content.room.title);
    }

    return <div className="session-view__metadata">{parts.join(' · ')}</div>;
  };

  return (
    <div id="page-document" className="view-wrapper session-view">
      <div className="session-view__layout">
        <div className="session-view__main">
          <header className="session-view__header">
            {track?.title && (
              <div className="session-view__track">{track.title}</div>
            )}

            <h1 className="session-view__title">{content.title}</h1>

            <SessionChips item={content} />

            {renderMetadataLine()}
          </header>

          {content.description && (
            <p className="session-view__description">{content.description}</p>
          )}

          <div className="session-view__body">
            {content.text?.data && (
              <section className="session-view__section">
                <h2 className="session-view__section-title">
                  {intl.formatMessage(messages.details)}
                </h2>
                <div
                  className="session-view__html"
                  dangerouslySetInnerHTML={{ __html: content.text.data }}
                />
              </section>
            )}

            {content.requirements?.data && (
              <section className="session-view__section">
                <h2 className="session-view__section-title">
                  {intl.formatMessage(messages.requirements)}
                </h2>
                <div
                  className="session-view__html"
                  dangerouslySetInnerHTML={{
                    __html: content.requirements.data,
                  }}
                />
              </section>
            )}

            {content.session_video && (
              <section className="session-view__section">
                <h2 className="session-view__section-title">
                  {intl.formatMessage(messages.video)}
                </h2>
                <Video url={content.session_video} />
              </section>
            )}

            {content.items && content.items.length > 0 && (
              <section className="session-view__section">
                <h2 className="session-view__section-title">
                  {intl.formatMessage(messages.materials)}
                </h2>
                <SessionMaterials content={content} />
              </section>
            )}
          </div>
        </div>

        {content.presenters && content.presenters.length > 0 && (
          <aside className="session-view__sidebar">
            <h2 className="session-view__sidebar-title">
              {intl.formatMessage(localMessages.speakers)}
            </h2>
            <div className="session-view__presenters">
              {content.presenters.map((presenter) => {
                const imgUrl = getSpeakerImageUrl(presenter);
                return (
                  <UniversalLink
                    key={presenter['@id']}
                    href={flattenToAppURL(presenter['@id'])}
                    className="session-view__presenter-link"
                  >
                    {imgUrl ? (
                      /* eslint-disable-next-line no-restricted-syntax */
                      <img
                        src={imgUrl}
                        alt={presenter.title}
                        className="session-view__presenter-avatar"
                        loading="lazy"
                      />
                    ) : (
                      <div className="session-view__presenter-avatar-placeholder" />
                    )}
                    <span className="session-view__presenter-name">
                      {presenter.title}
                    </span>
                  </UniversalLink>
                );
              })}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};

SessionView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    text: PropTypes.shape({
      data: PropTypes.string,
    }),
    session_track: PropTypes.any,
    session_level: PropTypes.any,
    session_audience: PropTypes.any,
    session_language: PropTypes.any,
    duration: PropTypes.any,
    start: PropTypes.string,
    room: PropTypes.shape({
      title: PropTypes.string,
    }),
    session_video: PropTypes.string,
    requirements: PropTypes.shape({
      data: PropTypes.string,
    }),
    items: PropTypes.arrayOf(PropTypes.any),
    presenters: PropTypes.arrayOf(PropTypes.any),
  }).isRequired,
};

export default SessionView;
